// ResultsLayout.jsx
import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { apiRequest } from "../../shared/api/apiClient";
import AnalyzeResults from "../AnalyzeResults";
import PageTransition from "../../shared/components/PageTransition";

const DEFAULT_OPTIONS = {
    multiqc: false,
    resistanceGenes: { resfinder: false, rgi: false },
    virulence: false,
    sccmec: false,
    kaptive: false,
    mlst: false,
    data: true,
};

function mapItemsToOptions(items = []) {
    const pagesAvailable = structuredClone(DEFAULT_OPTIONS);

    items.forEach((res) => {
        if (res === "kaptive") pagesAvailable.kaptive = true;
        else if (res === "mlst") pagesAvailable.mlst = true;
        else if (res === "resfinder" || res === "resfinderfq")
            pagesAvailable.resistanceGenes.resfinder = true;
        else if (res === "virulence_rgi") pagesAvailable.resistanceGenes.rgi = true;
        else if (res === "virulence" || res === "virulence_fastq")
            pagesAvailable.virulence = true;
        else if (res === "multiqc") pagesAvailable.multiqc = true;
        else if (res === "sccmec") pagesAvailable.sccmec = true;
    });

    return pagesAvailable;
}

function pickFirstAvailable(options) {
    if (options.multiqc) return "quality";
    if (options.resistanceGenes.resfinder) return "resistance/resfinder";
    if (options.resistanceGenes.rgi) return "resistance/rgi";
    if (options.virulence) return "virulence";
    if (options.sccmec) return "sccmec";
    if (options.kaptive) return "kaptive";
    if (options.mlst) return "mlst";
    return "download";
}

function isAllowedPath(subpath, options) {
    if (subpath === "" || subpath === "/") return true;
    if (subpath.startsWith("/quality")) return !!options.multiqc;
    if (subpath.startsWith("/virulence")) return !!options.virulence;
    if (subpath.startsWith("/sccmec")) return !!options.sccmec;
    if (subpath.startsWith("/kaptive")) return !!options.kaptive;
    if (subpath.startsWith("/mlst")) return !!options.mlst;

    if (subpath === "/resistance" || subpath === "/resistance/") {
        return true;
    }
    if (subpath.startsWith("/resistance/resfinder"))
        return !!options.resistanceGenes.resfinder;
    if (subpath.startsWith("/resistance/rgi")) return !!options.resistanceGenes.rgi;

    if (subpath.startsWith("/download")) return true;
    if (subpath.startsWith("/citations")) return true;

    return false;
}

export default function ResultsLayout() {
    const { id } = useParams();
    const token = process.env.REACT_APP_ATLAS_TOKEN;
    const { pathname } = useLocation();

    const [options, setOptions] = useState(DEFAULT_OPTIONS);
    const [refgen, setRefgen] = useState(""); // 👈 nuevo
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function loadOptions() {
            setLoading(true);
            setError(null);

            try {
                const params = new URLSearchParams({
                    token,
                    study_id: id,
                });

                const endpoint = `list_folders.php?${params.toString()}`;

                const resp = await apiRequest(endpoint, {
                    method: "GET",
                });

                const next = mapItemsToOptions(resp?.items);

                if (!cancelled) {
                    setOptions(next);
                    setRefgen(resp?.refgen || ""); // 👈 guardas refgen
                    setLoading(false);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e);
                    setLoading(false);
                }
            }
        }

        loadOptions();

        return () => {
            cancelled = true;
        };
    }, [id, token]);

    const base = useMemo(() => `/analyze/results/${id}`, [id]);

    const requestedSubpath = useMemo(() => {
        return pathname.startsWith(base) ? pathname.slice(base.length) : "";
    }, [pathname, base]);

    const firstAvailable = useMemo(() => pickFirstAvailable(options), [options]);

    if (loading) {
        return (
            <PageTransition>
                <div style={{ padding: 16 }}>Cargando resultados…</div>
            </PageTransition>
        );
    }

    if (error) {
        return (
            <PageTransition>
                <div style={{ padding: 16 }}>
                    No se pudieron cargar las opciones de resultados.
                </div>
            </PageTransition>
        );
    }

    if (!isAllowedPath(requestedSubpath, options)) {
        return <Navigate to={`${base}/${firstAvailable}`} replace />;
    }

    return (
        <PageTransition>
            <AnalyzeResults options={options} refgen={refgen} /> {/* 👈 lo pasas */}
        </PageTransition>
    );
}
